import { useState, type FormEvent } from 'react';
import { productMenu } from '@/mocks/homeData';

const FORM_ADDR = 'https://readdy.ai/api/form/dan8vtl1jbh5ebt7fca0';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function InquiryForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get('contact_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }
    formData.delete('contact_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const okCode = parsed?.code === 'OK';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && okCode && !isSpam) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || 'We could not send your enquiry. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setFormError(error instanceof Error ? error.message : 'Network error. Please try again.');
    }
  };

  return (
    <div className="w-full rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
      <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">Send Us an Enquiry</h2>
      <p className="mt-2 text-sm text-foreground-600">
        Fill in the form below and our team will get back to you within one business day.
      </p>

      <form data-readdy-form id="contact-enquiry-form" onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-xs font-semibold text-foreground-700 mb-1.5">
              Full Name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              placeholder="Your name"
              className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-xs font-semibold text-foreground-700 mb-1.5">
              Phone Number
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              required
              placeholder="+91 XXXXX XXXXX"
              className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-foreground-700 mb-1.5">
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="you@example.com"
              className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
          </div>
          <div>
            <label htmlFor="enquiryType" className="block text-xs font-semibold text-foreground-700 mb-1.5">
              Enquiry Type
            </label>
            <select
              id="enquiryType"
              name="enquiryType"
              defaultValue=""
              className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 cursor-pointer"
            >
              <option value="">Select an option</option>
              <option value="Product Enquiry">Product Enquiry</option>
              <option value="Bulk / Wholesale Order">Bulk / Wholesale Order</option>
              <option value="Distributorship">Distributorship</option>
              <option value="Third Party Manufacturing">Third Party Manufacturing</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="product" className="block text-xs font-semibold text-foreground-700 mb-1.5">
            Product Interest
          </label>
          <select
            id="product"
            name="product"
            defaultValue=""
            className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 cursor-pointer"
          >
            <option value="">Select a category</option>
            {productMenu.map((item) => (
              <option key={item.id} value={item.label}>
                {item.label}
              </option>
            ))}
            <option value="Bulk / Distribution">Bulk / Distribution</option>
          </select>
        </div>

        <div>
          <label htmlFor="message" className="block text-xs font-semibold text-foreground-700 mb-1.5">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            maxLength={500}
            required
            placeholder="Tell us what you are looking for (max 500 characters)"
            className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 resize-none"
          ></textarea>
        </div>

        <input
          className="form-hp-field"
          type="text"
          name="contact_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {status === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base"></i>
              Sending...
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill text-base"></i>
              Submit Enquiry
            </>
          )}
        </button>

        {status === 'success' ? (
          <p className="flex items-start gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
            <i className="ri-checkbox-circle-fill mt-0.5"></i>
            Thank you! Your enquiry has been received. Our team will contact you shortly.
          </p>
        ) : null}

        {status === 'error' ? (
          <p className="flex items-start gap-2 text-sm text-accent-800 bg-accent-50 border border-accent-200 rounded-md px-4 py-3">
            <i className="ri-error-warning-fill mt-0.5"></i>
            {formError}
          </p>
        ) : null}
      </form>
    </div>
  );
}